import { Tooltip } from "~/components/Tooltip";
import { cn } from "~/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { FeatureNames } from "~/modules/feature_flag/flags";
import { FeatureFlag } from "~/modules/feature_flag/components/FeatureFlag";

// // // //

export interface ButtonGroupOption {
    label: string;
    value: string;
    count?: number;
    tooltip?: string;
    testID?: string;
    icon?: IconDefinition;
    /** Extra class names applied to this option's icon. */
    iconClassName?: string;
    /** Disable this individual option, regardless of the group's disabled state. */
    disabled?: boolean;
    featureFlag?: FeatureNames;
    analyticsLabel?: string;
    analyticsEventProperties?: Record<string, any>;
}

type ButtonGroupSize = "sm" | "md" | "lg";

interface Props {
    title?: string | null;
    options: Array<ButtonGroupOption>;
    value?: string;
    onValueChange: (value: string) => void;
    disabled?: boolean;
    analyticsEventName?: string;
    size?: ButtonGroupSize;
    /** Stretch the control to fill its container, with equal-width buttons. */
    fullWidth?: boolean;
    className?: string;
}

const SIZE_STYLES: Record<
    ButtonGroupSize,
    {
        title: string;
        container: string;
        button: string;
        icon: string;
        count: string;
    }
> = {
    sm: {
        title: "text-sm",
        container: "gap-1.5 p-1 rounded-md",
        button: "gap-1.5 px-2.5 py-1.5 text-xs rounded-sm",
        icon: "h-2.5 w-2.5",
        count: "px-1.5 py-0.5 text-[10px]",
    },
    md: {
        title: "text-base",
        container: "gap-2 p-1.5 rounded-md",
        button: "gap-2 px-3.5 py-1 text-sm rounded-sm",
        icon: "h-3 w-3",
        count: "px-2 py-0.5 text-xs",
    },
    lg: {
        title: "text-lg",
        container: "gap-2 p-2 rounded-xl",
        button: "gap-2.5 px-4 py-2 text-base rounded-lg",
        icon: "h-3.5 w-3.5",
        count: "px-2 py-0.5 text-sm",
    },
};

/**
 * ButtonGroup
 * A segmented single-select control
 */
export function ButtonGroup(props: Props) {
    const {
        title,
        options,
        value,
        onValueChange,
        disabled,
        size = "sm",
        fullWidth = false,
        className,
    } = props;

    const sizeStyles = SIZE_STYLES[size];

    const currentValue = value || options[0]?.value;

    return (
        <div
            className={cn(
                "flex items-center gap-2 select-none",
                fullWidth && "w-full",
                className,
            )}
        >
            {title && (
                <h2
                    className={cn(
                        "font-semibold text-foreground whitespace-nowrap",
                        sizeStyles.title,
                    )}
                >
                    {title}
                </h2>
            )}
            <div
                className={cn(
                    fullWidth ? "flex flex-1" : "inline-flex",
                    "bg-muted",
                    sizeStyles.container,
                )}
            >
                {options.map((option) => {
                    const isActive = currentValue === option.value;
                    const isDisabled = !!disabled || !!option.disabled;

                    return (
                        <FeatureFlag
                            key={option.value}
                            feature={option.featureFlag}
                        >
                            <Tooltip
                                key={option.value}
                                placement="top"
                                tooltipContent={
                                    option.tooltip ? (
                                        <div className="w-full max-w-sm px-3 py-2 text-sm">
                                            {option.tooltip}
                                        </div>
                                    ) : null
                                }
                            >
                                {({ setTriggerRef }) => (
                                    <button
                                        ref={setTriggerRef}
                                        key={option.value}
                                        type="button"
                                        data-testid={option.testID}
                                        onClick={() => {
                                            if (isDisabled) return;

                                            onValueChange(option.value);
                                        }}
                                        disabled={isDisabled}
                                        className={cn(
                                            "relative z-10 flex items-center font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 whitespace-nowrap",
                                            sizeStyles.button,
                                            fullWidth &&
                                                "flex-1 justify-center",
                                            {
                                                "bg-card text-foreground ring-1 ring-border shadow-sm":
                                                    isActive,
                                                "text-muted-foreground":
                                                    !isActive,
                                                "hover:text-foreground transition-colors":
                                                    !isDisabled && !isActive,
                                                "opacity-50 cursor-not-allowed":
                                                    isDisabled,
                                            },
                                        )}
                                    >
                                        {option.icon && (
                                            <FontAwesomeIcon
                                                icon={option.icon}
                                                className={cn(
                                                    sizeStyles.icon,
                                                    option.iconClassName,
                                                )}
                                            />
                                        )}
                                        <span>{option.label}</span>
                                        {option.count !== undefined && (
                                            <span
                                                className={cn(
                                                    "rounded leading-none",
                                                    sizeStyles.count,
                                                    {
                                                        "bg-muted text-foreground":
                                                            isActive,
                                                        "bg-card text-muted-foreground":
                                                            !isActive,
                                                    },
                                                )}
                                            >
                                                {option.count}
                                            </span>
                                        )}
                                    </button>
                                )}
                            </Tooltip>
                        </FeatureFlag>
                    );
                })}
            </div>
        </div>
    );
}
